import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FileSignature, Save } from 'lucide-react';
import { streamSse } from './sse';
import { providerLabel } from './ProviderToggle';

const API = process.env.REACT_APP_BACKEND_URL;

export const ReportDraft = ({ caseId, provider, onDone }) => {
  const [content, setContent] = useState('');
  const [meta, setMeta] = useState(null);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/cases/${caseId}/ai/report-draft`, { withCredentials: true })
      .then(({ data }) => { setContent(data.content || ''); setMeta(data); })
      .catch(() => {});
  }, [caseId]);

  const generate = async () => {
    setRunning(true);
    setContent('');
    try {
      await streamSse(`/api/cases/${caseId}/ai/report-draft`, { provider }, (evt) => {
        if (evt.t) setContent((prev) => prev + evt.t);
        if (evt.draft) setMeta(evt.draft);
      });
      toast.success('Draft ready. Review it, then export the PDF.');
      onDone?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRunning(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/cases/${caseId}/ai/report-draft`, { content }, { withCredentials: true });
      setMeta(data);
      toast.success('Draft saved');
    } catch {
      toast.error('Could not save draft');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={generate} disabled={running} data-testid="ai-report-generate-btn"
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50">
          <FileSignature className="w-4 h-4" />
          <span>{running ? 'Writing...' : `${content ? 'Redraft' : 'Draft'} findings with ${providerLabel(provider)}`}</span>
        </button>
        <button onClick={save} disabled={running || saving || !content} data-testid="ai-report-save-btn"
          className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50">
          <Save className="w-4 h-4" /> <span>{saving ? 'Saving...' : 'Save edits'}</span>
        </button>
        {meta?.updated_at && <span className="text-xs text-gray-500" data-testid="ai-report-meta">Last updated {new Date(meta.updated_at).toLocaleString()}{meta.provider ? ` · ${providerLabel(meta.provider)}` : ''}</span>}
      </div>
      <textarea value={content} onChange={(e) => setContent(e.target.value)} readOnly={running} rows={14} data-testid="ai-report-textarea"
        placeholder="Your AI-drafted findings will appear here. Edit freely; the saved text is added to the Findings section of the PDF export."
        className="w-full border border-gray-300 rounded-lg p-3 text-sm normal-case leading-relaxed focus:outline-none focus:ring-2 focus:ring-purple-500" />
    </div>
  );
};
